// CallsBookings.js — Reports > Calls and Bookings (SS-4278)
//
// Per location and date range: the incoming calls, how many were about booking or
// rescheduling, and what the voice AI booked / rescheduled; next to it the conversations the
// AI text agent handled (SMS, web chat, email) and what it booked / rescheduled; then every
// booking and reschedule with its patient. One location (?bid=) or the locations picked in
// the shared Locations picker (?bids=, ReportDownloadButton.js). Every time is shown in the
// location's own zone, never the browser's. Download CSV is the api's two-section CSV; Print
// prints the whole report in landscape, as does a plain Ctrl+P.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faPrint } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";
import { cqAPI } from "../../lib/Auth";

const PRINT_ROOT_ID = "calls-bookings-print";
const DATE_PRESETS = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 4 weeks",
  "Month to Date",
  "Quarter to date",
  "Year to date",
  "Custom",
];

//the column groups of the strip and of the By location table, in order
export const CALL_COLUMNS = [
  { key: "calls_received", label: "Received", stat: "Calls received" },
  { key: "about_booking", label: "About booking/rescheduling", stat: "About booking/rescheduling" },
  { key: "booked", label: "Booked", stat: "Booked" },
  { key: "rescheduled", label: "Rescheduled", stat: "Rescheduled" },
  { key: "calls_with_booking", label: "With a booking or reschedule" },
];
export const TEXT_WEB_COLUMNS = [
  { key: "text_web_conversations", label: "Conversations", stat: "Conversations" },
  { key: "text_web_about_booking", label: "About booking/rescheduling", stat: "About booking/rescheduling" },
  { key: "text_web_booked", label: "Booked", stat: "Booked" },
  { key: "text_web_rescheduled", label: "Rescheduled", stat: "Rescheduled" },
];
const COUNT_KEYS = [...CALL_COLUMNS, ...TEXT_WEB_COLUMNS].map((column) => column.key);

const NAME_NOT_CAPTURED = "Name not captured";
const DASH = "—";

//Print and a plain Ctrl+P share these rules: the report is a scroll box on screen, and on
//paper it must flow over as many landscape pages as it needs, light even in dark mode, with
//the table headers repeated and no row or location header split from its rows.
const REPORT_PRINT_RULES = `
  #${PRINT_ROOT_ID} {
    overflow: visible !important;
    max-height: none !important;
    height: auto !important;
    width: 100% !important;
    padding: 0 !important;
  }
  .no-print { display: none !important; }
  #${PRINT_ROOT_ID}, #${PRINT_ROOT_ID} * {
    color: #1A202C !important;
    background-color: transparent !important;
    border-color: #CBD5E0 !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  #${PRINT_ROOT_ID} .cb-muted { color: #718096 !important; }
  #${PRINT_ROOT_ID} .cb-badge-booked { background-color: #C6F6D5 !important; color: #22543D !important; }
  #${PRINT_ROOT_ID} .cb-badge-rescheduled { background-color: #BEE3F8 !important; color: #2A4365 !important; }
  #${PRINT_ROOT_ID} thead th, #${PRINT_ROOT_ID} .cb-total td { background-color: #EDF2F7 !important; }
  #${PRINT_ROOT_ID} .cb-table-wrap { overflow: visible !important; }
  #${PRINT_ROOT_ID} table { width: 100% !important; min-width: 0 !important; table-layout: auto !important; font-size: 9pt !important; }
  #${PRINT_ROOT_ID} th, #${PRINT_ROOT_ID} td { padding: 3px 5px !important; font-size: 9pt !important; }
  #${PRINT_ROOT_ID} thead { display: table-header-group; }
  #${PRINT_ROOT_ID} tr, #${PRINT_ROOT_ID} .cb-avoid-break { break-inside: avoid; page-break-inside: avoid; }
  #${PRINT_ROOT_ID} .cb-location-header { break-after: avoid; page-break-after: avoid; break-inside: avoid; page-break-inside: avoid; }
`;
const PAGE_RULE = "@page { size: landscape; margin: 10mm; }";

//the Print button (react-to-print copies the report into its own frame)
export const PRINT_PAGE_STYLE = `${PAGE_RULE}
@media print {
  html, body {
    height: auto !important;
    overflow: visible !important;
    background: #FFFFFF !important;
    color: #1A202C !important;
    color-scheme: light !important;
  }
  ${REPORT_PRINT_RULES}
}`;

//a plain Ctrl+P prints the page itself: lift the app shell's fixed-height scroll boxes and
//leave the menu out. Mounted with the report, so no other page is affected.
export const CTRL_P_STYLE = `${PAGE_RULE}
@media print {
  html, body, #root, #container, .main-wrapper, .content-wrapper {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    background: #FFFFFF !important;
    color-scheme: light !important;
  }
  #container { display: block !important; }
  #container > :not(.main-wrapper), .main-wrapper > :not(.content-wrapper) { display: none !important; }
  ${REPORT_PRINT_RULES}
}`;

const formatCount = (value) => (Number(value) || 0).toLocaleString("en-US");

export const sumLocations = (locations) => COUNT_KEYS.reduce((acc, key) => ({
  ...acc,
  [key]: (locations || []).reduce((sum, location) => sum + (Number(location[key]) || 0), 0),
}), {});

//most calls first; a text-only location sorts by its AI conversations
export const sortLocations = (locations) => [...(locations || [])].sort((a, b) => (
  (b.calls_received || 0) - (a.calls_received || 0)
  || (b.text_web_conversations || 0) - (a.text_web_conversations || 0)
  || String(a.location_name || "").localeCompare(String(b.location_name || ""))
));

//America/New_York -> 'New York'
export const zoneCity = (zone) => (zone ? zone.split("/").pop().replace(/_/g, " ") : "");

export const formatInZone = (value, zone, format) => {
  if (!value) {
    return DASH;
  }
  const date = zone ? moment.tz(value, zone) : moment(value);
  return date.isValid() ? date.format(format) : DASH;
};

export const formatTime = (value, zone) => formatInZone(value, zone, "MMM D, h:mm A");
export const formatAppointment = (value, zone) => formatInZone(value, zone, "ddd, MMM D, h:mm A");

const EMPTY_DATA = { locations: [], appointments: [] };

//a shared link's range, or null when it is missing, malformed or backwards
export const rangeFromHash = (hash) => {
  const hashParams = new URLSearchParams((hash || "").replace(/^#/, ""));
  const start = moment(hashParams.get("startDate") || "", "YYYY-MM-DD", true);
  const end = moment(hashParams.get("endDate") || "", "YYYY-MM-DD", true);
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
    return null;
  }
  const preset = hashParams.get("selectedRange");
  return {
    dates: [start.startOf("day").toDate(), end.endOf("day").toDate()],
    selectedRange: DATE_PRESETS.includes(preset) ? preset : "Custom",
  };
};

const rangeForPreset = (value) => {
  switch (value) {
    case "Today":
      return [moment().startOf("day"), moment().endOf("day")];
    case "Yesterday":
      return [moment().subtract(1, "days").startOf("day"), moment().subtract(1, "days").endOf("day")];
    case "Last 7 days":
      return [moment().subtract(6, "days").startOf("day"), moment().endOf("day")];
    case "Last 4 weeks":
      return [moment().subtract(4, "weeks").startOf("day"), moment().endOf("day")];
    case "Month to Date":
      return [moment().startOf("month"), moment().endOf("day")];
    case "Quarter to date":
      return [moment().startOf("quarter"), moment().endOf("day")];
    case "Year to date":
      return [moment().startOf("year"), moment().endOf("day")];
    case "Custom":
    default:
      return [null, null];
  }
};

function SummaryRow({ title, columns, totals, testId }) {
  const statBg = useColorModeValue("gray.50", "gray.700");
  return (
    <Box mb={3} className="cb-avoid-break" data-testid={testId}>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>{title}</Text>
      {/* not a responsive column count: Chakra's breakpoints are screen-only media queries,
          so on paper the strip would fall back to its narrowest layout */}
      <SimpleGrid minChildWidth="170px" spacing={2}>
        {columns.filter((column) => column.stat).map((column) => (
          <Stat key={column.key} px={3} py={2} borderWidth="1px" borderRadius="md" bg={statBg}>
            <StatLabel className="cb-muted">{column.stat}</StatLabel>
            <StatNumber fontSize="xl">{formatCount(totals[column.key])}</StatNumber>
          </Stat>
        ))}
      </SimpleGrid>
    </Box>
  );
}

function ActionBadge({ action }) {
  const booked = action === "Booked";
  return (
    <Badge
      colorScheme={booked ? "green" : "blue"}
      variant="subtle"
      className={booked ? "cb-badge-booked" : "cb-badge-rescheduled"}
    >
      {action}
    </Badge>
  );
}

function LocationCounts({ location }) {
  return (
    <Box fontSize="sm">
      <Text>
        <Text as="span" fontWeight="semibold">Calls: </Text>
        {`${formatCount(location.calls_received)} received · ${formatCount(location.about_booking)} about booking/rescheduling · `
          + `${formatCount(location.booked)} booked · ${formatCount(location.rescheduled)} rescheduled · `
          + `${formatCount(location.calls_with_booking)} with a booking or reschedule`}
      </Text>
      <Text>
        <Text as="span" fontWeight="semibold">Text / web (AI): </Text>
        {`${formatCount(location.text_web_conversations)} conversations · `
          + `${formatCount(location.text_web_about_booking)} about booking/rescheduling · `
          + `${formatCount(location.text_web_booked)} booked · ${formatCount(location.text_web_rescheduled)} rescheduled`}
      </Text>
    </Box>
  );
}

function LocationDetail({ location, rows }) {
  const city = zoneCity(location.zone);
  return (
    <Box mb={6} data-testid="calls-bookings-location">
      <Box className="cb-location-header" mb={2}>
        <Flex align="baseline" wrap="wrap" gap={2}>
          <Heading as="h3" size="sm">{location.location_name}</Heading>
          {city && <Text fontSize="sm" className="cb-muted" color="gray.500">{`Times in ${city} time`}</Text>}
        </Flex>
        <LocationCounts location={location} />
      </Box>
      {rows.length === 0 ? (
        <Text fontSize="sm" className="cb-muted" color="gray.500">No AI bookings or reschedules in this range.</Text>
      ) : (
        <Box overflowX="auto" className="cb-table-wrap">
          <Table size="sm" variant="simple" sx={{ th: { textTransform: "none", letterSpacing: "normal" } }}>
            <Thead>
              <Tr>
                <Th>Patient</Th>
                <Th>Phone</Th>
                <Th>Channel</Th>
                <Th>Call Time</Th>
                <Th>Appointment</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, index) => (
                <Tr key={`${row.time}-${index}`} data-testid="calls-bookings-row">
                  <Td>
                    {row.patient_name
                      ? row.patient_name
                      : <Text as="span" color="gray.500" className="cb-muted">{NAME_NOT_CAPTURED}</Text>}
                  </Td>
                  <Td>{row.phone || DASH}</Td>
                  <Td>{row.channel}</Td>
                  <Td>{formatTime(row.time, row.zone || location.zone)}</Td>
                  <Td>{formatAppointment(row.appointment_time, row.zone || location.zone)}</Td>
                  <Td><ActionBadge action={row.action} /></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

function ByLocationTable({ locations, totals }) {
  const groupBg = useColorModeValue("gray.50", "gray.700");
  return (
    <Box mb={6} data-testid="calls-bookings-by-location">
      <Heading as="h3" size="sm" mb={2} className="cb-location-header">By location</Heading>
      <Box overflowX="auto" className="cb-table-wrap">
        <Table size="sm" variant="simple" sx={{ th: { textTransform: "none", letterSpacing: "normal" } }}>
          <Thead>
            <Tr>
              {/* no rowSpan: every header row spans the same 10 columns */}
              <Th />
              <Th colSpan={CALL_COLUMNS.length} textAlign="center" bg={groupBg} borderLeftWidth="1px">Calls</Th>
              <Th colSpan={TEXT_WEB_COLUMNS.length} textAlign="center" bg={groupBg} borderLeftWidth="1px">Text / web (AI)</Th>
            </Tr>
            <Tr>
              <Th>Location</Th>
              {CALL_COLUMNS.map((column, index) => (
                <Th key={column.key} isNumeric borderLeftWidth={index === 0 ? "1px" : undefined}>{column.label}</Th>
              ))}
              {TEXT_WEB_COLUMNS.map((column, index) => (
                <Th key={column.key} isNumeric borderLeftWidth={index === 0 ? "1px" : undefined}>{column.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {locations.map((location) => (
              <Tr key={String(location.bid)} data-testid="calls-bookings-location-row">
                <Td>{location.location_name}</Td>
                {COUNT_KEYS.map((key, index) => (
                  <Td
                    key={key}
                    isNumeric
                    borderLeftWidth={index === 0 || index === CALL_COLUMNS.length ? "1px" : undefined}
                  >
                    {formatCount(location[key])}
                  </Td>
                ))}
              </Tr>
            ))}
            <Tr className="cb-total" fontWeight="bold" data-testid="calls-bookings-total-row">
              <Td fontWeight="bold">Total</Td>
              {COUNT_KEYS.map((key, index) => (
                <Td
                  key={key}
                  isNumeric
                  fontWeight="bold"
                  borderLeftWidth={index === 0 || index === CALL_COLUMNS.length ? "1px" : undefined}
                >
                  {formatCount(totals[key])}
                </Td>
              ))}
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function Footnote() {
  return (
    <Box fontSize="xs" className="cb-muted cb-avoid-break" color="gray.500" mt={4} data-testid="calls-bookings-footnote">
      <Text fontWeight="semibold" mb={1}>How these numbers are counted</Text>
      <Text>Calls received: every incoming call to the location in the date range. Blocked calls are excluded.</Text>
      <Text>
        About booking/rescheduling (calls): calls whose call analysis shows the caller wanted to schedule a new
        appointment or reschedule one. Each call counts once.
      </Text>
      <Text>
        Booked / Rescheduled: appointments the AI booked or rescheduled. Two appointments booked in one conversation
        count as two.
      </Text>
      <Text>With a booking or reschedule: calls in which the AI booked or rescheduled at least one appointment.</Text>
      <Text>
        Text / web conversations: conversations handled by the AI text agent (SMS, web chat, email), spam excluded.
        Texts the AI did not handle are not counted.
      </Text>
      <Text>
        About booking/rescheduling (text / web): conversations whose analysis shows booking intent, or in which the AI
        booked or rescheduled. Text conversations are analysed after the patient&apos;s next conversation starts, so
        this count is a floor: recent conversations may not be analysed yet.
      </Text>
      <Text>
        Call Time is when the call (or text / web conversation) took place; Appointment is the slot booked, or the new
        slot of a reschedule. All times are in the
        location&apos;s own time zone. &quot;{NAME_NOT_CAPTURED}&quot; means the patient&apos;s name was not recorded.
      </Text>
    </Box>
  );
}

function CallsBookingsReport() {
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [dateRange, setDateRange] = useState([moment().startOf("day").toDate(), moment().endOf("day").toDate()]);
  const [hashReady, setHashReady] = useState(false);
  const [startDate, endDate] = dateRange;
  const hasCompleteRange = !!(startDate && endDate);
  const { colorMode } = useColorMode();
  //only the newest request may fill the page: a slower answer for an earlier range must not
  //overwrite the one on screen
  const latestRequest = useRef(0);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: PRINT_PAGE_STYLE,
    documentTitle: "calls-and-bookings",
  });

  const fetchReport = async (start, end) => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    const isCurrent = () => requestId === latestRequest.current;
    setIsLoading(true);
    setLoadError(false);
    try {
      const response = await cqAPI.get(
        `/reports/calls-bookings?rangeBeg=${moment(start).format("L")}&rangeEnd=${moment(end).format("L")}`
      );
      if (!isCurrent()) {
        return;
      }
      if (response?.type === "ok") {
        setData({
          locations: response.data?.locations || [],
          appointments: response.data?.appointments || [],
        });
      } else {
        setData(EMPTY_DATA);
        setLoadError(true);
      }
    } catch (error) {
      if (!isCurrent()) {
        return;
      }
      console.error("Error fetching the calls and bookings report:", error);
      setData(EMPTY_DATA);
      setLoadError(true);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  //init from the hash on mount (shareable), as Appointment Confirmations does; a link with no
  //usable range opens on Today
  useEffect(() => {
    const shared = rangeFromHash(window.location.hash);
    if (shared) {
      setDateRange(shared.dates);
      setSelectedRange(shared.selectedRange);
    }
    setHashReady(true);
  }, []);

  //keep the URL hash in sync. Replaced, not pushed: the page reads its range from the hash
  //only when it opens, so a Back step through old ranges would leave the screen and the CSV
  //(which reads the hash) on different dates
  useEffect(() => {
    if (!hashReady) {
      return;
    }
    const hash = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#${hash}`);
  }, [hashReady, startDate, endDate, selectedRange]);

  //run the report for every complete range; an incomplete one shows nothing rather than the
  //previous range's numbers
  useEffect(() => {
    if (!hashReady) {
      return;
    }
    if (startDate && endDate) {
      fetchReport(startDate, endDate);
      return;
    }
    latestRequest.current += 1;
    setData(EMPTY_DATA);
    setLoadError(false);
    setIsLoading(false);
  }, [hashReady, startDate, endDate]);

  const handlePreset = (value) => {
    const [start, end] = rangeForPreset(value);
    setSelectedRange(value);
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const locations = useMemo(() => sortLocations(data.locations), [data.locations]);
  const totals = useMemo(() => sumLocations(data.locations), [data.locations]);
  const rowsByLocation = useMemo(() => (data.appointments || []).reduce((acc, row) => {
    const key = String(row.bid);
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {}), [data.appointments]);

  const renderCustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>{date.toLocaleString("default", { month: "long", year: "numeric" })}</Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  return (
    //textAlign: the app shell centres text (.App in AppUI.css)
    <Box id={PRINT_ROOT_ID} ref={printRef} p={4} overflowY="auto" maxHeight="100vh" width="100%" textAlign="left">
      <style>{CTRL_P_STYLE}</style>

      {/* Controls — never printed */}
      <Flex mb={4} align="center" wrap="wrap" gap={2} className="no-print" data-testid="calls-bookings-controls">
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handlePreset(e.target.value)}
          width="200px"
          aria-label="Date range"
        >
          {DATE_PRESETS.map((preset) => <option key={preset} value={preset}>{preset}</option>)}
        </Select>
        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />
        <HStack ml="auto" spacing={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FontAwesomeIcon icon={faPrint} />}
            onClick={handlePrint}
            isDisabled={isLoading || loadError || !hasCompleteRange}
          >
            Print
          </Button>
          {/* the last child on purpose: with nothing after it, Download CSV takes the api's
              CSV (a by_location and an appointments section, each row with its location)
              rather than scraping the tables on screen (ReportDownloadButton.js) */}
          {hasCompleteRange && !loadError && <Flex data-report-download-target className="no-print" />}
        </HStack>
      </Flex>

      <Box mb={4}>
        <Heading as="h2" size="md">Calls and bookings</Heading>
      </Box>

      {!hasCompleteRange ? (
        <Text my={6} className="cb-muted" color="gray.500">Choose a start and an end date to run the report.</Text>
      ) : isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="40vh" width="100%">
          <Spinner color="blue.500" size="xl" />
        </Flex>
      ) : loadError ? (
        <Text color="red.500">Could not load the report. Please try again.</Text>
      ) : (
        <>
          <SummaryRow title="Calls" columns={CALL_COLUMNS} totals={totals} testId="calls-bookings-summary-calls" />
          <SummaryRow
            title="Text / web (AI)"
            columns={TEXT_WEB_COLUMNS}
            totals={totals}
            testId="calls-bookings-summary-text-web"
          />

          {locations.length === 0 && (
            <Text my={6} className="cb-muted" color="gray.500">
              No calls, AI text / web conversations or AI bookings in this range.
            </Text>
          )}

          {locations.length > 1 && <ByLocationTable locations={locations} totals={totals} />}

          {locations.map((location) => (
            <LocationDetail
              key={String(location.bid)}
              location={location}
              rows={rowsByLocation[String(location.bid)] || []}
            />
          ))}

          <Footnote />
        </>
      )}
    </Box>
  );
}

export default CallsBookingsReport;
